import { useQuery } from "@tanstack/react-query";
import { from, unwrap } from "@vixio/data";
import type { StatusMap } from "@vixio/ui";

export const SEVERITY: StatusMap = {
  baixo: { label: "baixo", tone: "neutral" },
  medio: { label: "médio", tone: "warn" },
  alto: { label: "alto", tone: "crit" },
  critico: { label: "crítico", tone: "crit" },
};
export const INCIDENT_STATUS: StatusMap = {
  aberto: { label: "aberto", tone: "crit" },
  contido: { label: "contido", tone: "warn" },
  comunicado: { label: "comunicado", tone: "info" },
  encerrado: { label: "encerrado", tone: "ok" },
};
export const INCIDENT_EVENT: Record<string, string> = {
  registro: "Registro", avaliacao: "Avaliação", contencao: "Contenção", comunicacao_anpd: "Comunicação à ANPD",
  comunicacao_titulares: "Comunicação aos titulares", nota: "Nota", encerramento: "Encerramento",
};
export const DPIA_STATUS: StatusMap = {
  rascunho: { label: "rascunho", tone: "neutral" },
  em_revisao: { label: "em revisão", tone: "warn" },
  aprovada: { label: "aprovada", tone: "ok" },
};
export const RESIDUAL: StatusMap = { baixo: { label: "baixo", tone: "ok" }, medio: { label: "médio", tone: "warn" }, alto: { label: "alto", tone: "crit" } };
export const CONTRACT_STATUS: StatusMap = {
  rascunho: { label: "rascunho", tone: "neutral" },
  vigente: { label: "vigente", tone: "ok" },
  encerrado: { label: "encerrado", tone: "neutral" },
};
export const LEGAL_BASIS: Record<string, string> = {
  consentimento: "Consentimento (art. 7º, I)",
  obrigacao_legal: "Obrigação legal (art. 7º, II)",
  politica_publica: "Política pública (art. 7º, III)",
  estudo_pesquisa: "Estudo por órgão de pesquisa (art. 7º, IV)",
  execucao_contrato: "Execução de contrato (art. 7º, V)",
  exercicio_direitos: "Exercício de direitos (art. 7º, VI)",
  protecao_vida: "Proteção da vida (art. 7º, VII)",
  tutela_saude: "Tutela da saúde (art. 7º, VIII)",
  interesse_legitimo: "Interesse legítimo (art. 7º, IX)",
  protecao_credito: "Proteção do crédito (art. 7º, X)",
};
export const SENSITIVE_BASIS: Record<string, string> = {
  consentimento_especifico: "Consentimento específico", obrigacao_legal: "Obrigação legal", politica_publica: "Política pública",
  estudo_pesquisa: "Estudo por órgão de pesquisa", exercicio_direitos: "Exercício de direitos", protecao_vida: "Proteção da vida",
  tutela_saude: "Tutela da saúde", prevencao_fraude: "Prevenção à fraude",
};
export const TRANSFER: Record<string, string> = {
  adequacao: "Adequação", clausulas_contratuais: "Cláusulas contratuais", normas_corporativas: "Normas corporativas", selos_certificados: "Selos e certificados",
  cooperacao_juridica: "Cooperação jurídica", protecao_vida: "Proteção da vida", autorizacao_anpd: "Autorização da ANPD", compromisso_cooperacao: "Compromisso de cooperação",
  politica_publica: "Política pública", consentimento_especifico: "Consentimento específico", contrato_ou_direitos: "Contrato ou exercício de direitos",
};
export const CONTROLLER: Record<string, string> = { controlador: "Controladora", controlador_conjunto: "Controladora conjunta", operador: "Operadora" };
export const STARTS_FROM: Record<string, string> = {
  coleta: "da coleta", ultimo_contato: "do último contato", fim_relacao: "do fim da relação", fim_contrato: "do fim do contrato",
  fim_exercicio_fiscal: "do fim do exercício fiscal", revogacao_consentimento: "da revogação do consentimento", enquanto_ativo: "enquanto ativo",
};
export const RET_ACTION: Record<string, string> = { anonimizar: "anonimizar", eliminar: "eliminar", revisar: "revisar" };
export const POLICY_KIND: Record<string, string> = { privacidade: "Política de privacidade", termos: "Termos de uso", cookies: "Política de cookies", uso_ia: "Uso de IA" };
export const CLAUSE_LABEL: Record<string, string> = {
  instrucoes_documentadas: "Tratamento só por instrução documentada",
  confidencialidade: "Confidencialidade",
  medidas_seguranca: "Medidas de segurança",
  suboperadores: "Suboperadores com autorização",
  apoio_direitos_titulares: "Apoio aos direitos dos titulares",
  comunicacao_incidentes: "Comunicação de incidentes",
  eliminacao_ou_devolucao: "Eliminação ou devolução ao fim",
  auditoria: "Auditoria",
  transferencia_internacional: "Transferência internacional",
};

export interface IncidentView {
  id: string;
  code: string;
  title: string;
  severity: string;
  relevant_risk: boolean | null;
  status: string;
  detected_at: string;
  affected_count: number | null;
  sensitive_data: boolean;
  anpd_due_on: string;
  subjects_due_on: string;
  anpd_notified_at: string | null;
  anpd_protocol: string | null;
  subjects_notified_at: string | null;
  closed_at: string | null;
  days_to_due: number | null;
  overdue: boolean;
  anpd_on_time: boolean | null;
  subjects_on_time: boolean | null;
}

export interface Incident extends IncidentView {
  description: string;
  occurred_at: string | null;
  data_categories: string[];
  subjects_channel: string | null;
  conclusion: string | null;
}

export interface Activity {
  id: string;
  code: string;
  name: string;
  purpose: string;
  legal_basis: string;
  sensitive_data: boolean;
  sensitive_legal_basis: string | null;
  children_data: boolean;
  consent_purpose: string | null;
  controller_role: string;
  data_categories: string[];
  data_subjects: string[];
  sources: string | null;
  systems: string[];
  sharing: Array<{ recipient: string; role?: string; purpose?: string }>;
  sharing_count?: number;
  international_transfer: boolean;
  transfer_countries: string[];
  transfer_mechanism: string | null;
  retention_data_type: string | null;
  retention_label?: string | null;
  retention_months?: number | null;
  retention_starts_from?: string | null;
  retention_action?: string | null;
  security_measures: string | null;
  status: string;
  version: number;
  updated_at: string;
  dpia_code?: string | null;
  dpia_status?: string | null;
}

export interface Retention {
  id: string;
  data_type: string;
  label: string;
  retention_months: number | null;
  starts_from: string;
  action: string;
  legal_ref: string | null;
  notes: string | null;
  last_run_at?: string | null;
  last_status?: string | null;
  last_affected?: number | null;
  last_note?: string | null;
}

export const DATA_REQUEST_KIND: Record<string, string> = { acesso: "Acesso (arquivo dos dados)", anonimizacao: "Anonimização" };
export const DATA_REQUEST_CHANNEL: Record<string, string> = {
  email: "E-mail", formulario: "Formulário", portal: "Portal do cliente", telefone: "Telefone", presencial: "Presencial", outro: "Outro",
};
export const DATA_REQUEST_STATUS: StatusMap = {
  recebido: { label: "recebido", tone: "info" },
  gerando: { label: "gerando arquivo", tone: "info" },
  pronto: { label: "arquivo pronto", tone: "accent" },
  concluido: { label: "concluído", tone: "ok" },
  recusado: { label: "recusado", tone: "neutral" },
  erro: { label: "erro", tone: "crit" },
};
export const RETENTION_RUN_STATUS: StatusMap = {
  aplicada: { label: "aplicada", tone: "ok" },
  simulada: { label: "simulada", tone: "info" },
  a_revisar: { label: "a revisar", tone: "warn" },
  sem_executor: { label: "sem executor", tone: "warn" },
  sem_prazo: { label: "sem prazo", tone: "neutral" },
  erro: { label: "erro", tone: "crit" },
};

export interface AnonymizationReport {
  person_id: string;
  reason: string;
  at: string;
  modules: Record<string, { anonimizado: Record<string, number>; retido: Array<{ dado: string; base_legal: string }> }>;
}

export interface DataRequest {
  id: string;
  code: string;
  person_id: string;
  person_name: string;
  kind: "acesso" | "anonimizacao";
  channel: string;
  note: string | null;
  received_at: string;
  due_on: string;
  status: "recebido" | "gerando" | "pronto" | "concluido" | "recusado" | "erro";
  export_sha256: string | null;
  export_bytes: number | null;
  export_generated_at: string | null;
  export_expires_at: string | null;
  export_purged_at: string | null;
  export_available: boolean;
  attempts: number;
  last_error: string | null;
  report: AnonymizationReport | null;
  resolution: string | null;
  completed_at: string | null;
  days_left: number;
  overdue: boolean;
  on_time: boolean | null;
}

export interface RetentionRun {
  id: string;
  run_id: string;
  data_type: string;
  label: string;
  action: string;
  status: string;
  affected: number;
  module: string | null;
  note: string | null;
  error: string | null;
  dry_run: boolean;
  ran_at: string;
  ran_by: string | null;
}

export interface Dpia {
  id: string;
  code: string;
  title: string;
  activity_id: string | null;
  scope: string;
  necessity: string;
  status: string;
  residual_risk: string | null;
  version: number;
  review_on: string | null;
  approved_at: string | null;
  updated_at: string;
}

export interface DpiaRisk {
  id: string;
  description: string;
  likelihood: number;
  severity: number;
  score: number | null;
  residual_likelihood: number | null;
  residual_severity: number | null;
  residual_score: number | null;
}

export interface DpiaMeasure {
  id: string;
  risk_id: string | null;
  description: string;
  status: "planejada" | "implantada";
  owner_label: string | null;
  due_on: string | null;
}

export interface ContractView {
  id: string;
  counterparty_name: string;
  counterparty_doc: string | null;
  vixio_role: string;
  service: string;
  status: string;
  signed_on: string | null;
  expires_on: string | null;
  days_to_expire: number | null;
  international_transfer: boolean;
  transfer_countries: string[];
  activity_ids: string[];
  document_code: string | null;
  missing_clauses: string[];
}

export interface PolicyVersion {
  id: string;
  kind: string;
  version: string;
  title: string;
  body: string;
  changes_summary: string | null;
  status: "rascunho" | "publicada";
  published_at: string | null;
  updated_at: string;
}

type U = string;
declare module "@vixio/data" {
  interface RpcRegistry {
    "compliance.appoint_dpo": { args: { p_org: U; p_name: string; p_email: string; p_phone?: string | null; p_person?: U | null; p_appointed_on?: string | null; p_notes?: string | null }; returns: U };
    "compliance.upsert_retention_rule": {
      args: { p_org: U; p_data_type: string; p_label: string; p_retention_months: number | null; p_starts_from: string; p_action: string; p_legal_ref?: string | null; p_notes?: string | null };
      returns: U;
    };
    "compliance.upsert_activity": {
      args: {
        p_org: U; p_code: string; p_name: string; p_purpose: string; p_legal_basis: string; p_data_categories: string[]; p_data_subjects: string[];
        p_controller_role?: string; p_sensitive_data?: boolean; p_sensitive_legal_basis?: string | null; p_consent_purpose?: string | null; p_children_data?: boolean;
        p_sources?: string | null; p_systems?: string[]; p_sharing?: Activity["sharing"]; p_international_transfer?: boolean; p_transfer_countries?: string[];
        p_transfer_mechanism?: string | null; p_retention_data_type?: string | null; p_security_measures?: string | null; p_reason?: string | null;
      };
      returns: U;
    };
    "compliance.set_activity_status": { args: { p_activity: U; p_status: string; p_reason: string }; returns: number };
    "compliance.upsert_dpia": { args: { p_org: U; p_code: string; p_title: string; p_scope: string; p_necessity: string; p_activity?: U | null; p_review_on?: string | null }; returns: U };
    "compliance.add_dpia_risk": { args: { p_dpia: U; p_description: string; p_likelihood: number; p_severity: number; p_residual_likelihood?: number | null; p_residual_severity?: number | null }; returns: U };
    "compliance.update_dpia_risk": { args: { p_risk: U; p_description: string; p_likelihood: number; p_severity: number; p_residual_likelihood: number | null; p_residual_severity: number | null }; returns: null };
    "compliance.add_dpia_measure": { args: { p_dpia: U; p_description: string; p_risk?: U | null; p_status?: string; p_owner_label?: string | null; p_due_on?: string | null }; returns: U };
    "compliance.set_measure_status": { args: { p_measure: U; p_status: string }; returns: null };
    "compliance.approve_dpia": { args: { p_dpia: U; p_residual_risk: string; p_opinion: string; p_review_on?: string | null }; returns: Record<string, unknown> };
    "compliance.register_incident": {
      args: {
        p_org: U; p_title: string; p_description: string; p_severity: string; p_detected_at?: string | null; p_occurred_at?: string | null; p_data_categories?: string[];
        p_sensitive_data?: boolean; p_affected_count?: number | null; p_relevant_risk?: boolean | null; p_idempotency_key?: string | null;
      };
      returns: U;
    };
    "compliance.assess_incident": { args: { p_incident: U; p_relevant_risk: boolean; p_note: string; p_severity?: string | null; p_affected_count?: number | null }; returns: null };
    "compliance.log_incident": { args: { p_incident: U; p_kind: string; p_note: string }; returns: U };
    "compliance.record_anpd_communication": { args: { p_incident: U; p_protocol: string; p_notified_at?: string | null; p_note?: string | null }; returns: null };
    "compliance.record_subjects_communication": { args: { p_incident: U; p_channel: string; p_notified_at?: string | null; p_note?: string | null }; returns: null };
    "compliance.close_incident": { args: { p_incident: U; p_conclusion: string }; returns: Record<string, unknown> };
    "compliance.upsert_processor_contract": {
      args: {
        p_org: U; p_counterparty_name: string; p_vixio_role: string; p_service: string; p_counterparty_doc?: string | null; p_clauses?: Record<string, boolean>;
        p_activity_ids?: U[]; p_international_transfer?: boolean; p_transfer_countries?: string[]; p_signed_on?: string | null; p_expires_on?: string | null;
        p_origin_type?: string | null; p_origin_id?: U | null; p_document_code?: string | null; p_id?: U | null;
      };
      returns: U;
    };
    "compliance.activate_processor_contract": { args: { p_contract: U }; returns: null };
    "compliance.end_processor_contract": { args: { p_contract: U; p_reason: string }; returns: null };
    "compliance.save_policy_draft": { args: { p_org: U; p_kind: string; p_version: string; p_title: string; p_body: string; p_changes_summary?: string | null }; returns: U };
    "compliance.publish_policy": { args: { p_policy_version: U }; returns: Record<string, unknown> };
    "compliance.required_clauses": { args: { p_international: boolean }; returns: string[] };
    "compliance.add_business_days": { args: { p_start: string; p_days: number }; returns: string };
    "compliance.open_data_request": {
      args: { p_org: U; p_person: U; p_kind: "acesso" | "anonimizacao"; p_channel?: string; p_note?: string | null; p_received_at?: string | null };
      returns: U;
    };
    "compliance.export_link": { args: { p_request: U }; returns: { bucket: string; storage_path: string; file_name: string; sha256: string } };
    "compliance.mark_data_request_delivered": { args: { p_request: U; p_note?: string | null }; returns: null };
    "compliance.regenerate_export": { args: { p_request: U }; returns: null };
    "compliance.fulfill_anonymization": { args: { p_request: U; p_confirm_name: string }; returns: AnonymizationReport };
    "compliance.reject_data_request": { args: { p_request: U; p_reason: string }; returns: null };
    "compliance.run_retention": { args: { p_org: U; p_dry_run?: boolean }; returns: { run_id: string; dry_run: boolean; rules: number; applied: number; affected: number; errors: number } };
  }
}

export function useActivities(orgId: string) {
  return useQuery({ queryKey: ["compliance", "ropa", orgId], queryFn: () => unwrap<Activity[]>(from("compliance", "v_ropa").select("*").eq("org_id", orgId).order("code")) });
}

export function useRetention(orgId: string) {
  return useQuery({ queryKey: ["compliance", "retention", orgId], queryFn: () => unwrap<Retention[]>(from("compliance", "retention_rules").select("*").eq("org_id", orgId).order("label")) });
}

export function usePurposes() {
  return useQuery({ queryKey: ["core", "consent_purposes"], queryFn: () => unwrap<Array<{ purpose: string; label: string }>>(from("core", "consent_purposes").select("purpose, label").order("label")), staleTime: 300_000 });
}

export const list = (s: string) => s.split(",").map((x) => x.trim()).filter(Boolean);
